import {html, css, nothing} from 'lit';
import {customElement, state} from 'lit/decorators.js';
import {unsafeHTML} from 'lit/directives/unsafe-html.js';
import {
  BehaviorSubject,
  combineLatest,
  interval,
  map,
  Observable,
  of,
  tap,
} from 'rxjs';
import Decimal from './break_infinity';
import global_css from './global-css';
import {Filterable} from './interfaces/filterable';
import {ToolTippy} from './mixins/tooltippy';
import {ri} from './runner-idle';
import {RxLitElement} from './rx-lit-element';
import {UnlockableLitElement} from './unlockable-lit-element';
import {any$} from './utils';

@customElement('ri-unlockables-entry')
export class UnlockablesEntry
  extends ToolTippy(UnlockableLitElement, {
    interactive: false,
    delay: [0, 0],
    duration: [null, null],
  })
  implements Filterable
{
  // #region Properties (11)
  override new$: Observable<boolean> = new BehaviorSubject(true);

  public static override styles = [
    global_css,
    css`
      :host {
        /* border: 1px rgb(var(--black, black)) solid; */
        --value-color: 180, 180, 180;
        --none-color: 20, 20, 20;
      }

      div {
        transition: 1s linear all;
        border: 1px rgb(var(--value-color, black)) solid;
        border-radius: 10px;
        /* background: inherit; */
      }

      .label {
        padding: 2px;
        /* mix-blend-mode: difference; */
        color: rgb(var(--none-color));
        font-weight: bold;
      }

      div.entry-unlocked {
        border-color: rgb(var(--black, black));
        border-width: 2px;
        border-radius: 0px;
      }

      div.entry-unlocked * {
        border-radius: 0px;
      }

      div.entry-locked {
        background: #fff8;
        border-color: rgb(var(--black, black));
        border-width: 2px;
      }

      smooth-progress {
        width: 100%;
      }
    `,
  ];

  @state() filterable!: UnlockableLitElement;

  public _newClass = '';
  public _progress: Decimal = new Decimal(0);
  public _unlockClass = '';
  public _unlockString = '';
  public _tooltipString = '';
  public override displayName = 'Unlockable';
  public override name = 'Unlockable';

  // #endregion Properties (11)

  // #region Constructors (1)

  constructor() {
    super();
    this.addEventListener('mouseover', () => {
      this.filterable.classList.add('unlockables-entry-hovered');
    });
    this.addEventListener('mouseout', () => {
      this.filterable.classList.remove('unlockables-entry-hovered');
    });
    this.unlock$ = of(true);
  }

  // #endregion Constructors (1)

  override get tags() {
    return this.filterable.tags;
  }

  // #region Public Methods (2)

  public override connectedCallback(): void {
    if (ri.seenEntries.has(this.filterable.name)) {
      this.new = false;
    }
    // console.log('connected callback', this);
    super.connectedCallback();
    interval(100).subscribe(() => this.requestUpdate());
    // this.subscribe('new', this.filterable.new$);
    this.subscribe('_progress', this.filterable.progress$);
    this.subscribe(
      '_newClass',
      combineLatest([
        this.new$,
        this.filterable.unlock$,
        this.filterable.new$,
      ]).pipe(
        map(([newP, filterableUnlocked, filterableNewP]) =>
          newP || (filterableUnlocked && filterableNewP) ? 'new' : ''
        ),
        tap((v) => (!v ? ri.seenEntries.add(this.filterable.name) : null))
      )
    );
    this.subscribe(
      '_unlockClass',
      this.filterable.unlock$.pipe(
        map((unlocked) => (unlocked ? 'entry-unlocked' : 'entry-locked'))
      )
    );
    this.subscribe(
      '_unlockString',
      combineLatest([this.filterable!.unlock$, this.filterable!.visible$]).pipe(
        map(([unlocked, visible]) => {
          if (unlocked || this.filterable.visited) {
            return this.filterable.displayName;
          } else if (visible) {
            return `${this.filterable.conditionString}`;
          } else {
            return 'Hidden Condition';
          }
        })
      )
    );
    this.subscribe(
      '_tooltipString',
      combineLatest([this.filterable!.unlock$, this.filterable!.visible$]).pipe(
        map(([unlocked, visible]) => {
          if (unlocked || this.filterable.visited) {
            return `Was unlocked by: ${this.filterable.conditionString}`;
          } else if (visible) {
            return `Will unlock: ${this.filterable.displayName}`;
          } else {
            return 'Hidden Reward';
          }
        })
      )
    );
  }

  public override render() {
    // if (!this.filterable) return nothing;
    // if (ri.settings.scrollLogToNew) setTimeout(() => this.scrollIntoView());
    return html`<div class="${this._newClass} ${this._unlockClass}">
      <smooth-progress
        .value=${this._progress}
        .square=${this._unlockClass === 'entry-unlocked' ? true : false}
      >
        <div class="label">${unsafeHTML(this._unlockString)}</div>
      </smooth-progress>
    </div>`;
  }

  // #endregion Public Methods (2)
  override _updateTooltip(): void {
    this.tooltip.setContent(this._tooltipString);
  }
}
